import { PropsWithClassNameWithChildren } from "@/types/ui";
import React, { ReactNode } from "react";
import { cn } from "@lib/utils";

export interface ContainerWithHeaderProps extends PropsWithClassNameWithChildren {
  headerLabel?: string;
  headerDescription?: string | ReactNode;
  headerAdditionalNode?: ReactNode;
  contentClassName?: string;
  headerClassName?: string;
}

/**
 * ContainerWithHeader is a React functional component that renders a container with a customizable header
 * and child content. The container supports additional styling and an optional additional node in the header.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components or elements to be displayed within the container body.
 * @param {string} [props.className] - Optional additional class name(s) to apply to the container for custom styling.
 * @param {string} [props.headerLabel] - Optional text to be displayed as the label in the header section.
 * @param {React.ReactNode} [props.headerAdditionalNode] - Optional React node to be displayed in the header, aligned to the right.
 *
 */
export const ContainerWithHeader = ({
  children,
  className,
  headerLabel,
  headerAdditionalNode,
  contentClassName,
  headerClassName,
}: ContainerWithHeaderProps) => (
  <div className={cn("flex-1 rounded-sm border", className)}>
    <div className={cn("flex items-center border-b bg-muted/50 px-4 py-2", headerClassName)}>
      <h2 className="font-medium">{headerLabel}</h2>
      <div className="ml-auto">{headerAdditionalNode}</div>
    </div>
    <div className={cn("p-2", contentClassName)}>{children}</div>
  </div>
);

/**
 * A functional React component that renders a container with a customizable header section
 * and content area. The header includes a label, description, and an optional additional node.
 *
 * @param {ContainerWithHeaderProps} props - The props object.
 * @param {React.ReactNode} props.children - The content to display in the main container below the header.
 * @param {string} [props.className] - Additional class names to apply to the header container wrapper.
 * @param {string} [props.contentClassName] - Additional class names to apply to the content container.
 * @param {string} [props.headerClassName] - Additional class names to apply to the header label.
 * @param {React.ReactNode} props.headerLabel - The main label to display in the header.
 * @param {string} [props.headerDescription] - An optional description to display below the header label.
 * @param {React.ReactNode} [props.headerAdditionalNode] - An optional element or node to display in the header, positioned to the right.
 */
export const ContainerWithHeaderSimple = ({
  children,
  className,
  contentClassName,
  headerClassName,
  headerLabel,
  headerDescription,
  headerAdditionalNode,
}: ContainerWithHeaderProps) => (
  <div className="flex flex-col gap-2 md:gap-4">
    <div
      className={cn(
        "flex flex-wrap items-center justify-around gap-2 bg-card pb-1 md:justify-start md:border-b dark:bg-background",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <h2 className={cn("text-xl font-bold tracking-tight", headerClassName)}>{headerLabel}</h2>
        {headerDescription && <p className="text-muted-foreground">{headerDescription}</p>}
      </div>
      {headerAdditionalNode && (
        <div className="ml-auto flex items-center pb-2">{headerAdditionalNode}</div>
      )}
    </div>
    {contentClassName && <div className={contentClassName}>{children}</div>}
    {!contentClassName && children}
  </div>
);
